<template>
  <div class="analysis">
    <!-- Loading Overlay -->
    <v-overlay :model-value="pageLoading" class="align-center justify-center">
      <v-progress-circular
        :size="70"
        :width="7"
        color="primary"
        indeterminate
      ></v-progress-circular>
    </v-overlay>

    <!-- Create New Ads Spend Form - 12 columns -->
    <v-row class="mb-6">
      <v-col cols="12">
        <v-card elevation="2">
          <v-card-title class="pb-2">
            <v-icon icon="mdi-plus-circle" class="mr-2"></v-icon>
            Thêm chi tiêu quảng cáo
          </v-card-title>
          <v-card-text>
            <v-form ref="createForm" @submit.prevent="createAdsSpend">
              <v-row>
                <v-col cols="12" md="3">
                  <v-text-field
                    v-model="newRecord.amount"
                    label="Số tiền chi tiêu"
                    type="number"
                    variant="outlined"
                    density="compact"
                    prepend-inner-icon="mdi-currency-usd"
                    :rules="[rules.required, rules.minAmount]"
                    @input="formatAmount"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="2">
                  <v-select
                    v-model="newRecord.platform"
                    label="Nền tảng"
                    :items="platforms"
                    variant="outlined"
                    density="compact"
                    prepend-inner-icon="mdi-web"
                    :rules="[rules.required]"
                  ></v-select>
                </v-col>
                <v-col cols="12" md="2">
                  <v-select
                    v-model="newRecord.depositor"
                    label="Người nạp"
                    :items="depositors"
                    variant="outlined"
                    density="compact"
                    prepend-inner-icon="mdi-account"
                    :rules="[rules.required]"
                  ></v-select>
                </v-col>
                <v-col cols="12" md="2">
                  <v-text-field
                    v-model="newRecord.deposit_date"
                    label="Ngày nạp"
                    type="date"
                    variant="outlined"
                    density="compact"
                    prepend-inner-icon="mdi-calendar"
                    :rules="[rules.required]"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="2">
                  <v-text-field
                    v-model="newRecord.note"
                    label="Ghi chú"
                    variant="outlined"
                    density="compact"
                    prepend-inner-icon="mdi-note"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="1">
                  <v-btn
                    color="primary"
                    variant="elevated"
                    @click="createAdsSpend"
                    :loading="creating"
                    block
                  >
                    <v-icon>mdi-plus</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Financial Summary - 12 columns -->
    <v-row class="mb-6">
      <v-col cols="12">
        <v-card elevation="2">
          <v-card-title class="pb-2">
            <v-icon icon="mdi-finance" class="mr-2"></v-icon>
            Tình hình tài chính tổng quan
          </v-card-title>
          <v-card-text>
            <v-row v-if="financialAnalysis && financialAnalysis.summary">
              <v-col cols="12" sm="6" md="3">
                <v-card class="summary-card" variant="tonal" color="success">
                  <v-card-text class="pa-4 text-center">
                    <v-icon size="32" color="success" class="mb-2">mdi-trending-up</v-icon>
                    <div class="text-h6 font-weight-bold">{{ formatCurrency(financialAnalysis.summary.total_profit) }}</div>
                    <div class="text-caption">Tổng lợi nhuận</div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-card class="summary-card" variant="tonal" color="warning">
                  <v-card-text class="pa-4 text-center">
                    <v-icon size="32" color="warning" class="mb-2">mdi-credit-card</v-icon>
                    <div class="text-h6 font-weight-bold">{{ formatCurrency(financialAnalysis.summary.total_ads_spend) }}</div>
                    <div class="text-caption">Tổng chi tiêu quảng cáo</div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-card class="summary-card" variant="tonal" :color="financialAnalysis.summary.net_profit >= 0 ? 'success' : 'error'">
                  <v-card-text class="pa-4 text-center">
                    <v-icon size="32" :color="financialAnalysis.summary.net_profit >= 0 ? 'success' : 'error'" class="mb-2">
                      {{ financialAnalysis.summary.net_profit >= 0 ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
                    </v-icon>
                    <div class="text-h6 font-weight-bold">{{ formatCurrency(financialAnalysis.summary.net_profit) }}</div>
                    <div class="text-caption">Lợi nhuận ròng</div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-card class="summary-card" variant="tonal" :color="financialAnalysis.summary.roi_percentage >= 0 ? 'info' : 'error'">
                  <v-card-text class="pa-4 text-center">
                    <v-icon size="32" :color="financialAnalysis.summary.roi_percentage >= 0 ? 'info' : 'error'" class="mb-2">mdi-percent</v-icon>
                    <div class="text-h6 font-weight-bold">{{ financialAnalysis.summary.roi_percentage.toFixed(1) }}%</div>
                    <div class="text-caption">ROI</div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
            <div v-else class="text-center py-8">
              <v-icon size="64" color="grey">mdi-finance</v-icon>
              <p class="text-grey mt-2">Không có dữ liệu tài chính</p>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Financial Analysis Chart - 12 columns -->
    <v-row class="mb-6">
      <v-col cols="12">
        <v-card elevation="2">
          <v-card-title class="pb-2">
            <v-icon icon="mdi-chart-line" class="mr-2"></v-icon>
            Biểu đồ chi tiêu và lợi nhuận hàng ngày
          </v-card-title>
                     <v-card-text>
             <!-- Always try to show chart if we have data -->
             <div v-if="chartData && chartData.length > 0">
               <LineChart 
                 title="Biểu đồ chi tiêu và lợi nhuận hàng ngày"
                 :data="chartData"
               />
               <div class="text-center mt-4">
                 <v-chip variant="tonal" size="small" class="mr-2" color="success">
                   <v-icon size="16" class="mr-1">mdi-circle</v-icon>
                   Lợi nhuận
                 </v-chip>
                 <v-chip variant="tonal" size="small" class="mr-2" color="warning">
                   <v-icon size="16" class="mr-1">mdi-circle</v-icon>
                   Chi tiêu quảng cáo
                 </v-chip>
                 <v-chip variant="tonal" size="small" color="info">
                   <v-icon size="16" class="mr-1">mdi-circle</v-icon>
                   Lợi nhuận ròng
                 </v-chip>
               </div>
               <!-- Show message if all data is zero -->
               <div v-if="isChartDataEmpty" class="text-center mt-4">
                 <v-alert type="info" variant="tonal" class="text-left">
                   <v-icon slot="prepend">mdi-information</v-icon>
                   <strong>Thông báo:</strong> Hiện tại chưa có hoạt động tài chính trong khoảng thời gian này.
                   <br>Hãy thêm chi tiêu quảng cáo hoặc có đơn hàng để xem biểu đồ có ý nghĩa hơn.
                 </v-alert>
               </div>
             </div>
             <div v-else class="text-center py-8">
               <v-icon size="64" color="grey">mdi-chart-line</v-icon>
               <p class="text-grey mt-2">Không có dữ liệu để hiển thị biểu đồ</p>
               <p class="text-caption">Đang tải dữ liệu hoặc chưa có thông tin tài chính...</p>
             </div>
           </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Pie Chart & Depositor Stats -->
    <v-row class="mb-6">
      <!-- Pie Chart - 6 columns -->
      <v-col cols="12" md="6">
        <v-card elevation="2">
          <v-card-title class="pb-2">
            <v-icon icon="mdi-chart-pie" class="mr-2"></v-icon>
            Phân tích người đầu tư
          </v-card-title>
          <v-card-text>
            <div v-if="pieChartData && pieChartData.length > 0" class="pie-chart-container">
              <canvas ref="pieChart" width="400" height="400"></canvas>
            </div>
            <div v-else class="text-center py-8">
              <v-icon size="64" color="grey">mdi-chart-pie</v-icon>
              <p class="text-grey mt-2">Không có dữ liệu để hiển thị</p>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Depositor Stats Table - 6 columns -->
      <v-col cols="12" md="6">
        <v-card elevation="2">
          <v-card-title class="pb-2">
            <v-icon icon="mdi-account-group" class="mr-2"></v-icon>
            Thống kê đầu tư
          </v-card-title>
          <v-card-text>
            <div v-if="depositorStats && depositorStats.length > 0">
              <!-- Desktop Table View -->
              <div v-if="!$vuetify.display.mobile" class="desktop-view">
                <v-table class="elevation-1">
                  <thead>
                    <tr>
                      <th>Người</th>
                      <th>Đầu tư</th>
                      <th>Lần nạp</th>
                      <th>%</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="stat in depositorStats" :key="stat.depositor">
                      <td>
                        <v-chip :color="getDepositorColor(stat.depositor)" variant="tonal" size="small">
                          {{ stat.depositor }}
                        </v-chip>
                      </td>
                      <td class="font-weight-bold">{{ formatCurrency(stat.total_investment) }}</td>
                      <td>{{ stat.total_deposits }}</td>
                      <td>
                        <v-chip color="info" variant="tonal" size="small">
                          {{ stat.investment_percentage.toFixed(1) }}%
                        </v-chip>
                      </td>
                    </tr>
                  </tbody>
                </v-table>
              </div>

              <!-- Mobile Card View -->
              <div v-else class="mobile-view">
                <div class="mobile-stats-container">
                  <v-card 
                    v-for="stat in depositorStats" 
                    :key="stat.depositor" 
                    class="mb-3 stat-card"
                    elevation="2">
                    <v-card-text>
                      <div class="d-flex justify-space-between align-center mb-2">
                        <v-chip :color="getDepositorColor(stat.depositor)" variant="tonal" size="small">
                          {{ stat.depositor }}
                        </v-chip>
                        <v-chip color="info" variant="tonal" size="small">
                          {{ stat.investment_percentage.toFixed(1) }}%
                        </v-chip>
                      </div>
                      
                      <div class="stat-info">
                        <div class="info-row">
                          <v-icon size="16" color="success" class="mr-2">mdi-currency-usd</v-icon>
                          <strong>Đầu tư:</strong>
                          <span class="ml-2 font-weight-bold text-success">{{ formatCurrency(stat.total_investment) }}</span>
                        </div>
                        
                        <div class="info-row">
                          <v-icon size="16" color="info" class="mr-2">mdi-counter</v-icon>
                          <strong>Lần nạp:</strong>
                          <span class="ml-2">{{ stat.total_deposits }}</span>
                        </div>
                      </div>
                    </v-card-text>
                  </v-card>
                </div>
              </div>
            </div>
            <div v-else class="text-center py-8">
              <v-icon size="64" color="grey">mdi-account-group</v-icon>
              <p class="text-grey mt-2">Không có dữ liệu thống kê</p>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Ads Spend Table - 12 columns -->
    <v-row>
      <v-col cols="12">
        <v-card elevation="2">
          <v-card-title class="pb-2">
            <div class="d-flex flex-column flex-md-row align-start align-md-center justify-space-between">
              <div class="d-flex align-center mb-3 mb-md-0">
                <v-icon icon="mdi-table" class="mr-2"></v-icon>
                Danh sách chi tiêu quảng cáo
              </div>
              <div class="d-flex flex-row flex-sm-row align-start align-sm-center gap-2">
                <v-text-field
                  v-model="search"
                  label="Tìm kiếm"
                  @input="onSearchInput"
                  clearable
                  prepend-inner-icon="mdi-magnify"
                  variant="outlined"
                  density="compact"
                  class="search-input filter-input"
                />
                <v-select
                  v-model="filters.platform"
                  label="Nền tảng"
                  :items="[{title: 'Tất cả', value: ''}, ...platforms]"
                  variant="outlined"
                  density="compact"
                  class="filter-input"
                  @update:model-value="onFilterChange"
                />
                <v-select
                  v-model="filters.depositor"
                  label="Người nạp"
                  :items="[{title: 'Tất cả', value: ''}, ...depositors]"
                  variant="outlined"
                  density="compact"
                  class="filter-input"
                  @update:model-value="onFilterChange"
                />
              </div>
            </div>
          </v-card-title>
          <v-card-text>
            <!-- Desktop Table View -->
            <div v-if="!$vuetify.display.mobile" class="desktop-view">
              <v-table class="elevation-1">
                <thead>
                  <tr>
                    <th @click="sort('id')" style="cursor:pointer">
                      ID
                      <v-icon :style="sortBy === 'id' ? '' : 'opacity:0.3'">
                        {{ sortBy === 'id' ? (sortOrder === 'ASC' ? 'mdi-arrow-up' : 'mdi-arrow-down') : 'mdi-arrow-up' }}
                      </v-icon>
                    </th>
                    <th @click="sort('amount')" style="cursor:pointer">
                      Số tiền
                      <v-icon :style="sortBy === 'amount' ? '' : 'opacity:0.3'">
                        {{ sortBy === 'amount' ? (sortOrder === 'ASC' ? 'mdi-arrow-up' : 'mdi-arrow-down') : 'mdi-arrow-up' }}
                      </v-icon>
                    </th>
                    <th @click="sort('platform')" style="cursor:pointer">
                      Nền tảng
                      <v-icon :style="sortBy === 'platform' ? '' : 'opacity:0.3'">
                        {{ sortBy === 'platform' ? (sortOrder === 'ASC' ? 'mdi-arrow-up' : 'mdi-arrow-down') : 'mdi-arrow-up' }}
                      </v-icon>
                    </th>
                    <th @click="sort('depositor')" style="cursor:pointer">
                      Người nạp
                      <v-icon :style="sortBy === 'depositor' ? '' : 'opacity:0.3'">
                        {{ sortBy === 'depositor' ? (sortOrder === 'ASC' ? 'mdi-arrow-up' : 'mdi-arrow-down') : 'mdi-arrow-up' }}
                      </v-icon>
                    </th>
                    <th @click="sort('deposit_date')" style="cursor:pointer">
                      Ngày nạp
                      <v-icon :style="sortBy === 'deposit_date' ? '' : 'opacity:0.3'">
                        {{ sortBy === 'deposit_date' ? (sortOrder === 'ASC' ? 'mdi-arrow-up' : 'mdi-arrow-down') : 'mdi-arrow-up' }}
                      </v-icon>
                    </th>
                    <th>Ghi chú</th>
                    <th class="text-center">Thao tác</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="record in allAdsSpendList" :key="record.id">
                    <td>{{ record.id }}</td>
                    <td class="font-weight-bold text-primary">{{ formatCurrency(Number(record.amount)) }}</td>
                    <td>
                      <v-chip 
                        :color="getPlatformColor(record.platform)" 
                        variant="tonal" 
                        size="small"
                      >
                        {{ getPlatformName(record.platform) }}
                      </v-chip>
                    </td>
                    <td>
                      <v-chip 
                        :color="getDepositorColor(record.depositor)" 
                        variant="tonal" 
                        size="small"
                      >
                        {{ record.depositor }}
                      </v-chip>
                    </td>
                    <td>{{ formatDate(record.deposit_date) }}</td>
                    <td>{{ record.note || '-' }}</td>
                    <td class="text-center">
                      <v-btn 
                        icon="mdi-eye" 
                        size="small" 
                        color="info" 
                        variant="tonal"
                        @click="viewDetail(record)"
                        class="mr-2"
                      ></v-btn>
                      <v-btn 
                         icon="mdi-pencil" 
                         size="small" 
                         color="primary" 
                         variant="tonal"
                         @click="openEditDialog(record)"
                         class="mr-2"
                       ></v-btn>
                      <v-btn 
                        icon="mdi-delete" 
                        size="small" 
                        color="error" 
                        variant="tonal"
                        @click="deleteRecord(record)"
                      ></v-btn>
                    </td>
                  </tr>
                </tbody>
              </v-table>
              <v-pagination
                v-model="pagination.page"
                :length="pagination.pages"
                @update:modelValue="onPageChange"
                class="custom-pagination"
              />
            </div>

            <!-- Mobile Card View -->
            <div v-else class="mobile-view">
              <div class="mobile-ads-container" ref="adsContainer">
                <v-card 
                  v-for="record in allAdsSpendList" 
                  :key="record.id" 
                  class="mb-3 ads-card"
                  elevation="2">
                  <v-card-text>
                    <div class="d-flex justify-space-between align-center mb-2">
                      <div class="record-id font-weight-bold">ID: {{ record.id }}</div>
                      <div class="amount font-weight-bold text-primary">{{ formatCurrency(Number(record.amount)) }}</div>
                    </div>
                    
                    <div class="ads-info">
                      <div class="info-row">
                        <v-icon size="16" color="purple" class="mr-2">mdi-apps</v-icon>
                        <strong>Nền tảng:</strong>
                        <v-chip 
                          :color="getPlatformColor(record.platform)" 
                          variant="tonal" 
                          size="x-small"
                          class="ml-2"
                        >
                          {{ getPlatformName(record.platform) }}
                        </v-chip>
                      </div>
                      
                      <div class="info-row">
                        <v-icon size="16" color="info" class="mr-2">mdi-account</v-icon>
                        <strong>Người nạp:</strong>
                        <v-chip 
                          :color="getDepositorColor(record.depositor)" 
                          variant="tonal" 
                          size="x-small"
                          class="ml-2"
                        >
                          {{ record.depositor }}
                        </v-chip>
                      </div>
                      
                      <div class="info-row">
                        <v-icon size="16" color="grey" class="mr-2">mdi-calendar</v-icon>
                        <strong>Ngày nạp:</strong>
                        <span class="ml-2">{{ formatDate(record.deposit_date) }}</span>
                      </div>
                      
                      <div class="info-row" v-if="record.note">
                        <v-icon size="16" color="orange" class="mr-2">mdi-note</v-icon>
                        <strong>Ghi chú:</strong>
                        <span class="ml-2">{{ record.note }}</span>
                      </div>
                    </div>
                    
                    <div class="d-flex justify-center mt-3 gap-2">
                      <v-btn 
                        color="info" 
                        size="small" 
                        @click="viewDetail(record)"
                        variant="tonal"
                      >
                        <v-icon class="mr-1">mdi-eye</v-icon>
                        Xem
                      </v-btn>
                      <v-btn 
                        color="primary" 
                        size="small" 
                        @click="openEditDialog(record)"
                        variant="tonal"
                      >
                        <v-icon class="mr-1">mdi-pencil</v-icon>
                        Sửa
                      </v-btn>
                      <v-btn 
                        color="error" 
                        size="small" 
                        @click="deleteRecord(record)"
                        variant="tonal"
                      >
                        <v-icon class="mr-1">mdi-delete</v-icon>
                        Xóa
                      </v-btn>
                    </div>
                  </v-card-text>
                </v-card>
              </div>
              
              <!-- Loading indicator for infinite scroll -->
              <div v-if="loadingMore" class="text-center py-4">
                <v-progress-circular indeterminate color="primary"></v-progress-circular>
                <div class="mt-2">Đang tải thêm...</div>
              </div>
              
              <!-- End of list indicator -->
              <div v-if="hasReachedEnd && allAdsSpendList.length > 0" class="text-center py-4 text-grey">
                <v-icon>mdi-check-circle</v-icon>
                <div>Đã hiển thị tất cả dữ liệu</div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Edit Dialog -->
    <v-dialog v-model="editDialog" max-width="600px">
      <v-card>
        <v-card-title class="text-h6">
          <v-icon icon="mdi-pencil" class="mr-2"></v-icon>
          Cập nhật chi tiêu quảng cáo
        </v-card-title>
        <v-card-text>
          <v-form ref="editForm">
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editRecord.amount"
                  label="Số tiền chi tiêu"
                  type="number"
                  variant="outlined"
                  density="compact"
                  prepend-inner-icon="mdi-currency-usd"
                  :rules="[rules.required, rules.minAmount]"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="editRecord.platform"
                  label="Nền tảng"
                  :items="platforms"
                  variant="outlined"
                  density="compact"
                  prepend-inner-icon="mdi-web"
                  :rules="[rules.required]"
                ></v-select>
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="editRecord.depositor"
                  label="Người nạp"
                  :items="depositors"
                  variant="outlined"
                  density="compact"
                  prepend-inner-icon="mdi-account"
                  :rules="[rules.required]"
                ></v-select>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editRecord.deposit_date"
                  label="Ngày nạp"
                  type="date"
                  variant="outlined"
                  density="compact"
                  prepend-inner-icon="mdi-calendar"
                  :rules="[rules.required]"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="editRecord.note"
                  label="Ghi chú"
                  variant="outlined"
                  density="compact"
                  prepend-inner-icon="mdi-note"
                  rows="3"
                ></v-textarea>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn 
            color="grey" 
            variant="text"
            @click="editDialog = false"
          >
            Hủy
          </v-btn>
          <v-btn 
            color="primary" 
            variant="elevated"
            @click="updateRecord"
            :loading="updating"
          >
            Cập nhật
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Detail Dialog -->
    <v-dialog v-model="detailDialog" max-width="500px">
      <v-card>
        <v-card-title class="text-h6">
          <v-icon icon="mdi-eye" class="mr-2"></v-icon>
          Chi tiết chi tiêu quảng cáo
        </v-card-title>
        <v-card-text>
          <v-list v-if="detailRecord" lines="two">
            <v-list-item>
              <v-list-item-title>ID</v-list-item-title>
              <v-list-item-subtitle>{{ detailRecord.id }}</v-list-item-subtitle>
            </v-list-item>
            <v-list-item>
              <v-list-item-title>Số tiền</v-list-item-title>
              <v-list-item-subtitle class="font-weight-bold text-primary">
                {{ formatCurrency(Number(detailRecord.amount)) }}
              </v-list-item-subtitle>
            </v-list-item>
            <v-list-item>
              <v-list-item-title>Nền tảng</v-list-item-title>
              <v-list-item-subtitle>
                <v-chip 
                  :color="getPlatformColor(detailRecord.platform)" 
                  variant="tonal" 
                  size="small"
                >
                  {{ getPlatformName(detailRecord.platform) }}
                </v-chip>
              </v-list-item-subtitle>
            </v-list-item>
            <v-list-item>
              <v-list-item-title>Người nạp</v-list-item-title>
              <v-list-item-subtitle>
                <v-chip 
                  :color="getDepositorColor(detailRecord.depositor)" 
                  variant="tonal" 
                  size="small"
                >
                  {{ detailRecord.depositor }}
                </v-chip>
              </v-list-item-subtitle>
            </v-list-item>
            <v-list-item>
              <v-list-item-title>Ngày nạp</v-list-item-title>
              <v-list-item-subtitle>{{ formatDate(detailRecord.deposit_date) }}</v-list-item-subtitle>
            </v-list-item>
            <v-list-item>
              <v-list-item-title>Ghi chú</v-list-item-title>
              <v-list-item-subtitle>{{ detailRecord.note || 'Không có ghi chú' }}</v-list-item-subtitle>
            </v-list-item>
            <v-list-item>
              <v-list-item-title>Ngày tạo</v-list-item-title>
              <v-list-item-subtitle>{{ formatDateTime(detailRecord.created_at) }}</v-list-item-subtitle>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn 
            color="primary" 
            variant="elevated"
            @click="detailDialog = false"
          >
            Đóng
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400px">
      <v-card>
        <v-card-title class="text-h6">
          <v-icon icon="mdi-delete" class="mr-2"></v-icon>
          Xác nhận xóa
        </v-card-title>
        <v-card-text>
          Bạn có chắc chắn muốn xóa bản ghi chi tiêu quảng cáo này không?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn 
            color="grey" 
            variant="text"
            @click="deleteDialog = false"
          >
            Hủy
          </v-btn>
          <v-btn 
            color="error" 
            variant="elevated"
            @click="confirmDelete"
            :loading="deleting"
          >
            Xóa
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Success/Error Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      top
    >
      <v-icon :icon="snackbar.icon" class="mr-2"></v-icon>
      {{ snackbar.message }}
    </v-snackbar>
  </div>
</template>

<script>
import axios from '@/http';
import LineChart from '@/components/LineChart.vue';

export default {
  name: 'Analysis',
  components: {
    LineChart
  },
  data() {
    return {
      // Loading states
      pageLoading: false,
      creating: false,
      updating: false,
      deleting: false,
      
      // Data
      adsSpendList: [],
      allAdsSpendList: [], // For infinite scroll
      chartData: [],
      pieChartData: [],
      depositorStats: [],
      financialAnalysis: null,
      
      // Pagination
      pagination: {
        page: 1,
        limit: 10,
        total: 0,
        pages: 1
      },
      
      // Infinite scroll
      loadingMore: false,
      hasReachedEnd: false,
      currentMobilePage: 1,
      
      // Filters & Search
      search: '',
      filters: {
        platform: '',
        depositor: '',
        date_start: '',
        date_stop: ''
      },
      sortBy: 'created_at',
      sortOrder: 'DESC',
      
      // Forms
      newRecord: {
        amount: '',
        platform: '',
        depositor: '',
        deposit_date: '',
        note: ''
      },
      editRecord: {},
      detailRecord: null,
      
      // Dialog states
      editDialog: false,
      detailDialog: false,
      deleteDialog: false,
      recordToDelete: null,
      
      // Options
      platforms: [
        { title: 'Facebook', value: 'facebook' },
        { title: 'TikTok', value: 'tiktok' },
        { title: 'Google', value: 'google' }
      ],
      depositors: [
        { title: 'T', value: 'T' },
        { title: 'Q', value: 'Q' },
        { title: 'A', value: 'A' },
        { title: 'H', value: 'H' },
        { title: 'S', value: 'S' },
        { title: 'B', value: 'B' }
      ],
      
      // Validation rules
      rules: {
        required: value => !!value || 'Trường này là bắt buộc',
        minAmount: value => value > 0 || 'Số tiền phải lớn hơn 0'
      },
      
      // Snackbar
      snackbar: {
        show: false,
        message: '',
        color: 'success',
        icon: 'mdi-check-circle',
        timeout: 3000
      }
         };
   },
   
   computed: {
     isChartDataEmpty() {
       if (!this.chartData || this.chartData.length === 0) return true;
       
       // Check if all values are 0
       return this.chartData.every(item => 
         item['Lợi nhuận'] === 0 && 
         item['Chi tiêu quảng cáo'] === 0 && 
         item['Lợi nhuận ròng'] === 0
       );
     }
   },
   
   mounted() {
    // Set default deposit date to today
    const today = new Date().toISOString().split('T')[0];
    this.newRecord.deposit_date = today;
    
    this.initializePage();
    this.setupInfiniteScroll();
  },

  beforeUnmount() {
    if (this.observer) {
      this.observer.disconnect();
    }
  },
  
  methods: {
    async initializePage() {
      this.pageLoading = true;
      try {
        await Promise.all([
          this.fetchAdsSpendList(),
          this.fetchFinancialAnalysis(),
          this.fetchDepositorStats()
        ]);
      } finally {
        this.pageLoading = false;
      }
    },
    
    async fetchAdsSpendList(append = false) {
      if (append) {
        this.loadingMore = true;
      }
      
      try {
        const params = {
          page: append ? this.currentMobilePage : this.pagination.page,
          limit: this.pagination.limit,
          sort_by: this.sortBy,
          sort_order: this.sortOrder
        };
        
        if (this.search) params.search = this.search;
        if (this.filters.platform) params.platform = this.filters.platform;
        if (this.filters.depositor) params.depositor = this.filters.depositor;
        if (this.filters.date_start) params.date_start = this.filters.date_start;
        if (this.filters.date_stop) params.date_stop = this.filters.date_stop;
        
        const response = await axios.get('/admin/ads-spend', { params });
        
        if (append) {
          // For mobile infinite scroll
          this.allAdsSpendList = [...this.allAdsSpendList, ...response.data.data];
          this.hasReachedEnd = response.data.data.length < this.pagination.limit || params.page >= response.data.pagination.pages;
        } else {
          // For desktop pagination or initial load
          this.adsSpendList = response.data.data;
          this.allAdsSpendList = response.data.data;
        }
        
        this.pagination = response.data.pagination;
      } catch (error) {
        console.error('Error fetching ads spend list:', error);
        this.showSnackbar('Lỗi khi tải danh sách chi tiêu quảng cáo', 'error');
      } finally {
        if (append) {
          this.loadingMore = false;
        }
      }
    },

    async loadMoreAdsSpend() {
      if (this.loadingMore || this.hasReachedEnd) return;
      
      this.currentMobilePage++;
      await this.fetchAdsSpendList(true);
    },

    resetMobileData() {
      this.allAdsSpendList = [];
      this.currentMobilePage = 1;
      this.hasReachedEnd = false;
    },

    setupInfiniteScroll() {
      if (!('IntersectionObserver' in window)) return;
      
      this.$nextTick(() => {
        if (this.$refs.adsContainer) {
          this.observer = new IntersectionObserver((entries) => {
            const target = entries[0];
            if (target.isIntersecting && this.$vuetify.display.mobile) {
              this.loadMoreAdsSpend();
            }
          }, {
            root: null,
            rootMargin: '200px',
            threshold: 0.1
          });
          
          // Create a sentinel element at the bottom
          const sentinel = document.createElement('div');
          sentinel.style.height = '1px';
          sentinel.style.background = 'transparent';
          this.$refs.adsContainer?.appendChild(sentinel);
          this.observer.observe(sentinel);
        }
      });
    },
    
    async fetchFinancialAnalysis() {
      try {
        const response = await axios.get('/admin/ads-spend/financial-analysis?days=30');
        this.financialAnalysis = response.data.data;
        
                 // Prepare chart data - ensure numbers are valid
         const dailyStats = response.data.data.daily_stats || [];
         
         // Always get the last 10 days regardless of data content
         let chartStats = dailyStats.slice(-10);
         
         // Ensure we always have some data to show
         if (chartStats.length === 0) {
           chartStats = dailyStats.slice(-5); // At least show last 5 days
         }
         
         this.chartData = chartStats.map(stat => {
           const profit = Number(stat.daily_profit) || 0;
           const adsSpend = Number(stat.daily_ads_spend) || 0;
           const netProfit = Number(stat.net_profit) || 0;
           
           return {
             date: this.formatDateForChart(stat.date),
             'Lợi nhuận': profit,
             'Chi tiêu quảng cáo': adsSpend,
             'Lợi nhuận ròng': netProfit
           };
         });
         
         // Debug log
         console.log('Chart data prepared:', this.chartData);
      } catch (error) {
        console.error('Error fetching financial analysis:', error);
        this.showSnackbar('Lỗi khi tải phân tích tài chính', 'error');
      }
    },
    
    async fetchDepositorStats() {
      try {
        const response = await axios.get('/admin/ads-spend/depositor-stats');
        this.depositorStats = response.data.data;
        
        // Prepare pie chart data
        this.pieChartData = this.depositorStats.map(stat => ({
          label: stat.depositor,
          value: stat.total_investment,
          percentage: stat.investment_percentage
        }));
        
        // Draw pie chart
        this.$nextTick(() => {
          this.drawPieChart();
        });
      } catch (error) {
        console.error('Error fetching depositor stats:', error);
        this.showSnackbar('Lỗi khi tải thống kê người đầu tư', 'error');
      }
    },
    
    drawPieChart() {
      if (!this.$refs.pieChart || !this.pieChartData.length) return;
      
      const canvas = this.$refs.pieChart;
      const ctx = canvas.getContext('2d');
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      const radius = Math.min(centerX, centerY) - 40;
      
      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Colors for each depositor
      const colors = ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40'];
      
      let currentAngle = 0;
      
      this.pieChartData.forEach((data, index) => {
        const sliceAngle = (data.percentage / 100) * 2 * Math.PI;
        
        // Draw slice
        ctx.beginPath();
        ctx.moveTo(centerX, centerY);
        ctx.arc(centerX, centerY, radius, currentAngle, currentAngle + sliceAngle);
        ctx.closePath();
        ctx.fillStyle = colors[index % colors.length];
        ctx.fill();
        
        // Draw label
        const labelAngle = currentAngle + sliceAngle / 2;
        const labelX = centerX + Math.cos(labelAngle) * (radius * 0.7);
        const labelY = centerY + Math.sin(labelAngle) * (radius * 0.7);
        
        ctx.fillStyle = '#fff';
        ctx.font = 'bold 14px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(data.label, labelX, labelY);
        ctx.fillText(`${data.percentage.toFixed(1)}%`, labelX, labelY + 15);
        
        currentAngle += sliceAngle;
      });
    },
    
    async createAdsSpend() {
      const { valid } = await this.$refs.createForm.validate();
      if (!valid) return;
      
      this.creating = true;
      try {
        await axios.post('/admin/ads-spend', this.newRecord);
        
        this.showSnackbar('Tạo chi tiêu quảng cáo thành công', 'success');
        this.resetCreateForm();
        this.fetchAdsSpendList();
        this.fetchFinancialAnalysis();
        this.fetchDepositorStats();
      } catch (error) {
        console.error('Error creating ads spend:', error);
        this.showSnackbar('Lỗi khi tạo chi tiêu quảng cáo', 'error');
      } finally {
        this.creating = false;
      }
    },
    
    async updateRecord() {
      const { valid } = await this.$refs.editForm.validate();
      if (!valid) return;
      
      this.updating = true;
      try {
        await axios.put(`/admin/ads-spend/${this.editRecord.id}`, this.editRecord);
        
        this.showSnackbar('Cập nhật chi tiêu quảng cáo thành công', 'success');
        this.editDialog = false;
        this.fetchAdsSpendList();
        this.fetchFinancialAnalysis();
        this.fetchDepositorStats();
      } catch (error) {
        console.error('Error updating ads spend:', error);
        this.showSnackbar('Lỗi khi cập nhật chi tiêu quảng cáo', 'error');
      } finally {
        this.updating = false;
      }
    },
    
    async confirmDelete() {
      this.deleting = true;
      try {
        await axios.delete(`/admin/ads-spend/${this.recordToDelete.id}`);
        
        this.showSnackbar('Xóa chi tiêu quảng cáo thành công', 'success');
        this.deleteDialog = false;
        this.fetchAdsSpendList();
        this.fetchFinancialAnalysis();
        this.fetchDepositorStats();
      } catch (error) {
        console.error('Error deleting ads spend:', error);
        this.showSnackbar('Lỗi khi xóa chi tiêu quảng cáo', 'error');
      } finally {
        this.deleting = false;
      }
    },
    
         openEditDialog(record) {
       this.editRecord = { 
         ...record,
         // Format date for input[type="date"] - needs YYYY-MM-DD format
         deposit_date: this.formatDateForInput(record.deposit_date)
       };
       this.editDialog = true;
       
       // Reset form validation after dialog opens
       this.$nextTick(() => {
         if (this.$refs.editForm) {
           this.$refs.editForm.resetValidation();
         }
       });
     },
    
    viewDetail(record) {
      this.detailRecord = { ...record };
      this.detailDialog = true;
    },
    
    deleteRecord(record) {
      this.recordToDelete = record;
      this.deleteDialog = true;
    },
    
         resetCreateForm() {
       const today = new Date().toISOString().split('T')[0];
       this.newRecord = {
         amount: '',
         platform: '',
         depositor: '',
         deposit_date: today,
         note: ''
       };
       this.$refs.createForm.resetValidation();
     },
    
    formatAmount() {
      // Remove non-numeric characters except decimal point
      this.newRecord.amount = this.newRecord.amount.replace(/[^\d]/g, '');
    },
    
    sort(column) {
      if (this.sortBy === column) {
        this.sortOrder = this.sortOrder === 'ASC' ? 'DESC' : 'ASC';
      } else {
        this.sortBy = column;
        this.sortOrder = 'ASC';
      }
      this.pagination.page = 1;
      this.resetMobileData();
      this.fetchAdsSpendList();
    },
    
    onSearchInput() {
      clearTimeout(this._searchTimeout);
      this._searchTimeout = setTimeout(() => {
        this.pagination.page = 1;
        this.resetMobileData();
        this.fetchAdsSpendList();
      }, 400);
    },
    
    onFilterChange() {
      this.pagination.page = 1;
      this.resetMobileData();
      this.fetchAdsSpendList();
    },
    
    onPageChange(newPage) {
      this.pagination.page = newPage;
      this.fetchAdsSpendList();
    },
    
    // Utility methods
    formatCurrency(amount) {
      return new Intl.NumberFormat('vi-VN', {
        style: 'currency',
        currency: 'VND'
      }).format(amount);
    },
    
    formatDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toLocaleDateString('vi-VN');
    },
    
         formatDateTime(dateStr) {
       if (!dateStr) return '';
       const date = new Date(dateStr);
       return date.toLocaleString('vi-VN');
     },
     
     formatDateForChart(dateStr) {
       if (!dateStr) return '';
       const date = new Date(dateStr);
       return date.toLocaleDateString('vi-VN', { 
         month: '2-digit', 
         day: '2-digit' 
       });
     },
     
     formatDateForInput(dateStr) {
       if (!dateStr) return '';
       const date = new Date(dateStr);
       // Format as YYYY-MM-DD for input[type="date"]
       return date.toISOString().split('T')[0];
     },
    
    getPlatformName(platform) {
      const platformMap = {
        facebook: 'Facebook',
        tiktok: 'TikTok',
        google: 'Google'
      };
      return platformMap[platform] || platform;
    },
    
    getPlatformColor(platform) {
      const colorMap = {
        facebook: 'blue',
        tiktok: 'pink',
        google: 'orange'
      };
      return colorMap[platform] || 'grey';
    },
    
    getDepositorColor(depositor) {
      const colorMap = {
        T: 'red',
        Q: 'blue',
        A: 'green',
        H: 'orange',
        S: 'purple',
        B: 'teal'
      };
      return colorMap[depositor] || 'grey';
    },
    
    showSnackbar(message, color = 'success') {
      this.snackbar.message = message;
      this.snackbar.color = color;
      this.snackbar.icon = color === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle';
      this.snackbar.show = true;
    }
  }
};
</script>

<style scoped>
.analysis {
  max-width: 1400px;
  margin: 0 auto;
}

.search-input {
  margin-bottom: 0;
}

.custom-pagination {
  margin-top: 20px;
}

.pie-chart-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

.pie-chart-container canvas {
  max-width: 100%;
  height: auto;
}

.font-weight-bold {
  font-weight: 600 !important;
}

.text-primary {
  color: rgb(var(--v-theme-primary)) !important;
}

/* Card styling */
.v-card {
  border-radius: 12px;
  border: none !important;
}

.v-card-title {
  background-color: transparent !important;
  border-bottom: none !important;
  padding: 20px 20px 12px 20px;
  font-weight: 600;
  font-size: 1.1rem;
  color: #2c3e50;
}

/* Form styling */
.v-text-field,
.v-select,
.v-textarea {
  margin-bottom: 8px;
}

/* Button styling */
.v-btn {
  text-transform: none;
  letter-spacing: 0.5px;
}

/* Table styling */
.v-table thead th {
  background-color: rgba(var(--v-theme-primary), 0.05);
  font-weight: 600;
}

.v-table tbody tr:hover {
  background-color: rgba(var(--v-theme-primary), 0.02);
}

 /* Summary cards */
 .summary-card {
   transition: transform 0.2s ease, box-shadow 0.2s ease;
   border-radius: 12px !important;
 }
 
 .summary-card:hover {
   transform: translateY(-2px);
   box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
 }
 
 .summary-card .text-h6 {
   font-size: 1.5rem !important;
   line-height: 1.2 !important;
 }
 
 .summary-card .text-caption {
   font-size: 0.85rem !important;
   text-transform: uppercase;
   letter-spacing: 0.5px;
   opacity: 0.8;
 }

 /* Responsive adjustments */
 @media (max-width: 768px) {
   .analysis {
     padding: 0 8px;
   }
   
   .pie-chart-container {
     min-height: 300px;
   }
   
   .pie-chart-container canvas {
     width: 300px;
     height: 300px;
   }
   
   .summary-card .text-h6 {
     font-size: 1.25rem !important;
   }
 }

/* Mobile Card Styles */
.mobile-view {
  padding: 0;
}

.mobile-stats-container,
.mobile-ads-container {
  padding: 8px 0;
}

.stat-card,
.ads-card {
  border-radius: 12px !important;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.stat-card:hover,
.ads-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

.stat-info,
.ads-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.info-row {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  min-height: 24px;
  font-size: 14px;
}

.info-row strong {
  min-width: 70px;
  font-weight: 600;
}

.info-row .ml-2 {
  flex: 1;
  word-break: break-word;
}

.record-id,
.amount {
  word-break: break-word;
  line-height: 1.3;
}

/* Desktop responsive adjustments */
@media (max-width: 1200px) {
  .desktop-view .v-table thead th {
    font-size: 13px;
    padding: 8px 4px;
  }
  
  .desktop-view .v-table tbody td {
    font-size: 13px;
    padding: 8px 4px;
  }
}

/* Desktop styles for filters */
.filter-input {
  min-width: 120px;
  max-width: 250px;
}

.search-input.filter-input {
  min-width: 200px;
  max-width: 250px;
}

/* Mobile specific styles */
@media (max-width: 768px) {
  .search-input {
    max-width: 100% !important;
    margin-bottom: 16px;
  }
  
  .v-card-title {
    flex-direction: column !important;
    align-items: stretch !important;
    gap: 16px;
  }
  
  .mobile-stats-container,
  .mobile-ads-container {
    padding: 4px 0;
  }
  
  .stat-card,
  .ads-card {
    margin-bottom: 12px !important;
  }
  
  .info-row {
    font-size: 13px;
  }
  
  .info-row strong {
    min-width: 65px;
    font-size: 13px;
  }
  
  .record-id,
  .amount {
    font-size: 15px;
  }

  /* Filter controls stacking */
  .d-flex.flex-row.align-start,
  .d-flex.flex-row.flex-sm-row,
  .d-flex.flex-row.flex-sm-row.align-start.align-sm-center.gap-2 {
    flex-direction: column !important;
    gap: 8px !important;
    align-items: stretch !important;
    width: 100% !important;
  }
  
  .d-flex.flex-row > *,
  .d-flex.flex-row.flex-sm-row > * {
    width: 100% !important;
    max-width: 100% !important;
    min-width: 100% !important;
  }
  
  /* Force full width for all filter inputs on mobile */
  .filter-input {
    width: 100% !important;
    max-width: 100% !important;
    min-width: 100% !important;
  }
  
  /* Target all Vuetify internal components */
  .filter-input .v-input,
  .filter-input .v-input__control {
    width: 100% !important;
  }
  
  .filter-input .v-field,
  .filter-input .v-field.v-field--appended,
  .filter-input .v-field.v-field--center-affix,
  .filter-input .v-field.v-field--prepended,
  .filter-input .v-field.v-field--variant-outlined,
  .filter-input .v-field.v-theme--light,
  .filter-input .v-field.v-locale--is-ltr {
    width: 100% !important;
  }
  
  .filter-input .v-field__overlay {
    width: 100% !important;
  }

  .summary-card .text-h6 {
    font-size: 1.25rem !important;
  }
}
</style>
